import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Constant from "../Utils/Constant";
import BackendConnector from '../Utils/BackendConnector';

// Minuscules sans accents, pour une recherche tolérante à la frappe
const normalize = (text) => (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const MIN_SEARCH_LENGTH = 3;

const TrekSection = () => {
    const [hikings, setHikings] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [displayedHikingsCount, setDisplayedHikingsCount] = useState(Constant.ITEMS_PER_PAGE);
    const navigate = useNavigate();

    // Active ou désactive un tag et réinitialise la pagination
    const toggleTag = (tagId) => {
        setSelectedTagIds(prev => prev.includes(tagId)
            ? prev.filter(id => id !== tagId)
            : [...prev, tagId]
        );
        setDisplayedHikingsCount(Constant.ITEMS_PER_PAGE);
    };

    // La recherche ne s'applique qu'à partir de MIN_SEARCH_LENGTH caractères
    const normalizedSearch = normalize(searchTerm.trim());
    const isSearchActive = normalizedSearch.length >= MIN_SEARCH_LENGTH;

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setDisplayedHikingsCount(Constant.ITEMS_PER_PAGE);
    };

    // Une randonnée doit porter au moins un des tags sélectionnés ET correspondre à la recherche
    const filteredHikings = hikings.filter(hiking => {
        const matchesTags = selectedTagIds.length === 0
            || (hiking.tags || []).some(tag => selectedTagIds.includes(tag.id));
        const matchesSearch = !isSearchActive
            || normalize(hiking.name).includes(normalizedSearch);
        return matchesTags && matchesSearch;
    });

    const hasActiveFilter = selectedTagIds.length > 0 || isSearchActive;

    // Fonction pour formater la date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { 
            year: 'numeric', 
            month: 'long' 
        });
    };
    
    const LoadingSkeleton = () => (
        <div className="row">
            {[...Array(Constant.ITEMS_PER_PAGE)].map((_, index) => (
                <div key={index} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4">
                    <div className="trek-card loading">
                        <div className="trek-image-container">
                            <div className="loading-shimmer">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{Constant.LOADING}</span>
                                </div>
                            </div>
                        </div>
                        <div className="trek-content">
                            <div className="placeholder-glow">
                                <span className="placeholder col-6" style={{height: '1rem', borderRadius: '4px'}}></span>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        setIsLoading(true);
        BackendConnector.fetchData('hikings/')
            .then(data => {
                // Trier les randonnées de la plus récente à la plus ancienne
                const sortedData = data.sort((a, b) => {
                    if (!a.date && !b.date) return 0;
                    if (!a.date) return 1; // Les randonnées sans date en dernier
                    if (!b.date) return -1;
                    return new Date(b.date) - new Date(a.date);
                });
                setHikings(sortedData);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Erreur lors du chargement des randonnées:', error);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        // Les filtres sont optionnels : en cas d'échec la liste reste utilisable
        BackendConnector.fetchData('hiking-tags/')
            .then(data => setTags(data))
            .catch(error => {
                console.error('Erreur lors du chargement des tags:', error);
            });
    }, []);

    return (
        <section id="treks" className="section">
            <div className="trek-section-container">
                <h2>{Constant.MY_TREKS}</h2>
                <p className="trek-intro">
                    {Constant.TREK_INTRO}
                </p>

                <div className="trek-filters">
                    <div className="trek-filters-start">
                    <div className="trek-search">
                        <svg
                            className="trek-search-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            <circle cx="11" cy="11" r="7" />
                            <line x1="16.5" y1="16.5" x2="21" y2="21" />
                        </svg>
                        <input
                            type="search"
                            className="trek-search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder={Constant.SEARCH_TREK_PLACEHOLDER}
                            aria-label={Constant.SEARCH_TREK_PLACEHOLDER}
                        />
                    </div>

                    <button
                        type="button"
                        className="trek-tag-filter trek-map-button"
                        onClick={() => navigate(Constant.HIKING_MAP_PATH)}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                            <line x1="8" y1="2" x2="8" y2="18" />
                            <line x1="16" y1="6" x2="16" y2="22" />
                        </svg>
                        {Constant.HIKING_MAP_BUTTON}
                    </button>
                    </div>

                    {tags.length > 0 && (
                        <div className="trek-tags-filter">
                            {tags.map(tag => {
                                const isActive = selectedTagIds.includes(tag.id);
                                return (
                                    <button
                                        key={tag.id}
                                        type="button"
                                        className={`trek-tag-filter${isActive ? ' active' : ''}`}
                                        aria-pressed={isActive}
                                        onClick={() => toggleTag(tag.id)}
                                    >
                                        {tag.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <LoadingSkeleton />
                ) : filteredHikings.length === 0 ? (
                    <div className="row">
                        <div className="col-12">
                            <div className="no-treks-message">
                                <div className="no-treks-icon">🥾</div>
                                <h3>{hasActiveFilter ? Constant.NO_TREKS_FOR_FILTER : Constant.NO_TREKS_AVAILABLE}</h3>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="row">
                            {filteredHikings.slice(0, displayedHikingsCount).map(hiking => (
                                <div key={hiking.id} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4">
                                    <div 
                                        className="trek-card clickable"
                                        onClick={() => navigate(`/hiking/${hiking.id}`)}
                                    >
                                        <div className="trek-image-container">
                                            {hiking.cover ? (
                                                <img 
                                                    src={hiking.cover} 
                                                    alt={hiking.name} 
                                                    className="trek-image"
                                                />
                                            ) : (
                                                <div className="trek-placeholder">
                                                    <i className="fas fa-mountain" style={{fontSize: '2rem', color: '#64748b'}}></i>
                                                    <p className="text-muted mb-0 mt-2">{Constant.NO_IMAGE_MESSAGE}</p>
                                                </div>
                                            )}
                                            <div className="trek-overlay">
                                                <div className="trek-info">
                                                    <h5 className="trek-title">{hiking.name}</h5>
                                                    <div className="trek-location">
                                                        <i className="fas fa-map-marker-alt"></i>
                                                        <span>{hiking.department}, {hiking.country}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Badge de date en overlay */}
                                            {hiking.date && (
                                                <div className="trek-date-badge">
                                                    {formatDate(hiking.date)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {filteredHikings.length > displayedHikingsCount && (
                            <div className="row">
                                <div className="col-12 text-center mt-4">
                                    <button 
                                        className="btn-view"
                                        onClick={() => setDisplayedHikingsCount(prev => prev + Constant.ITEMS_PER_PAGE)}
                                    >
                                        {Constant.SHOW_MORE_ALBUMS}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

export default TrekSection;